import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Collapse,
  IconButton,
  LinearProgress,
  Typography,
  useTheme,
  Divider,
  Paper,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import axios from "axios";
import AuthService from "../../utils/AuthService";
import ApiMap from "../../utils/ApiMap";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";

const LeaderBoard = ({ desc, courseId }) => {
  const [expanded, setExpanded] = useState(false);
  const [userScores, setUserScores] = useState(null);
  const theme = useTheme();

  useEffect(() => {
    const fetchScores = async () => {
      try {
        const resp = await axios(
          `${ApiMap.COURSE_LEADERBOARD}/course/${courseId}/studentscores`,
          AuthService.getAuthHeader()
        );
        const sorted = [...resp.data].sort((a, b) => b.userScore - a.userScore);
        setUserScores(sorted);
      } catch (error) {
        console.error("Failed to fetch leaderboard:", error);
      }
    };
    if (!userScores) fetchScores();
  }, [courseId]);

  const handleExpandClick = () => setExpanded((prev) => !prev);

  if (!userScores) {
    return (
      <Box textAlign="center" mt={2}>
        <Typography variant="body2" color="text.secondary">
          Loading Leaderboard...
        </Typography>
        <LinearProgress sx={{ mt: 1, width: "80%", mx: "auto" }} />
      </Box>
    );
  }

  if (userScores.length < 1) {
    return (
      <Box
        mt={2}
        p={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        sx={{
          backgroundColor: "#f9f9f9",
          border: "1px dashed #ccc",
          borderRadius: 2,
        }}
      >
        <EmojiEventsOutlinedIcon
          color="disabled"
          sx={{ fontSize: 36, mb: 1 }}
        />
        <Typography variant="body2" color="text.secondary">
          {desc || "Leaderboard will appear once students start scoring."}
        </Typography>
      </Box>
    );
  }

  const renderRows = (scores, start = 1) =>
    scores.map((s, i) => (
      <Box
        key={i}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={1.5}
        py={1}
        sx={{
          backgroundColor:
            theme.palette.mode === "light"
              ? i % 2 === 0
                ? "grey.200"
                : "grey.100"
              : i % 2 === 0
              ? "grey.800"
              : "grey.900",
          borderRadius: 1.5,
          mb: 0.8,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0px 1px 2px rgba(0,0,0,0.04)",
          fontSize: "0.75rem", // globally reduce text size
        }}
      >
        {/* Rank Number */}
        <Avatar
          sx={{
            bgcolor: theme.palette.secondary.main,
            color: "#fff",
            fontSize: "0.7rem",
            width: 24,
            height: 24,
            mr: 1.2,
          }}
        >
          {start + i}
        </Avatar>

        {/* User Avatar */}
        <Avatar
          alt={s.fname}
          src={s.profilePic}
          sx={{
            width: 32,
            height: 32,
            mr: 1.2,
          }}
        />

        {/* Name */}
        <Typography
          noWrap
          sx={{
            flexGrow: 1,
            maxWidth: "45%",
            fontWeight: 500,
            fontSize: "0.75rem",
            color: theme.palette.text.primary,
          }}
        >
          {s.fname} {s.lname?.charAt(0)}.
        </Typography>

        {/* Score */}
        <Tooltip title="Score / Total">
          <Typography
            fontWeight="600"
            fontSize="0.75rem"
            color="primary"
            sx={{ minWidth: 60, textAlign: "right" }}
          >
            {s.userScore ?? 0}/{s.totalScore ?? 0}
          </Typography>
        </Tooltip>
      </Box>
    ));

  return (
    <Paper
      elevation={3}
      sx={{
        mt: 3,
        p: 2,
        borderRadius: 3,
        backgroundColor: theme.palette.mode === "dark" ? "grey.900" : "grey.50",
      }}
    >
      {/* Top 3 */}
      {renderRows(userScores.slice(0, 3), 1)}

      {/* Collapsible Extra Ranks */}
      {userScores.length > 3 && (
        <>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            {renderRows(userScores.slice(3, 10), 4)}
          </Collapse>

          <Box textAlign="center" mt={1}>
            <IconButton
              onClick={handleExpandClick}
              size="small"
              color="primary"
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ cursor: "pointer" }}
            >
              {expanded ? "Show Less" : "Show More"}
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default LeaderBoard;
